import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Receipt, Upload, Camera, FileText, Clock, CheckCircle, 
  XCircle, PoundSterling, Loader2, AlertTriangle, RefreshCw
} from "lucide-react";
import { API } from "@/App";

const statusConfig = {
  submitted: { label: "Submitted", color: "bg-blue-100 text-blue-700", icon: Clock },
  approved: { label: "Approved", color: "bg-green-100 text-green-700", icon: CheckCircle },
  paid: { label: "Paid", color: "bg-emerald-100 text-emerald-700", icon: PoundSterling },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-700", icon: XCircle }
};

export const ContractorInvoicesPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [totals, setTotals] = useState({});
  const [activeTab, setActiveTab] = useState("all");
  
  // Re-upload modal state
  const [reuploadModal, setReuploadModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [reuploadFile, setReuploadFile] = useState(null);
  const [reuploadAmount, setReuploadAmount] = useState("");
  const [reuploadNotes, setReuploadNotes] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    checkOnboardingAndFetch();
  }, []);

  const checkOnboardingAndFetch = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/contractor-management/insurance/status`, { headers });
      
      const status = response.data.status;
      if (status && status !== "active") {
        // Redirect to onboarding if not active
        navigate("/contractor/onboard");
        return;
      }
      
      fetchInvoices();
    } catch (error) {
      if (error.response?.status === 403) {
        // Access denied - redirect to onboarding
        const detail = error.response?.data?.detail || "";
        if (detail.includes("CONTRACTOR_")) {
          navigate("/contractor/onboard");
          return;
        }
      }
      console.error("Error checking status:", error);
      fetchInvoices();
    }
  };

  const fetchInvoices = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/contractor/invoices`, { headers });
      
      setInvoices(response.data.invoices || []);
      setTotals(response.data.totals || {});
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Access denied. Please complete onboarding.");
        navigate("/contractor/onboard");
        return;
      }
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  const openReuploadModal = (invoice) => {
    setSelectedInvoice(invoice);
    setReuploadAmount(invoice.amount?.toString() || "");
    setReuploadNotes("");
    setReuploadFile(null);
    setReuploadModal(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        toast.error("Invalid file type. Please upload PDF, JPG, or PNG.");
        return;
      }
      setReuploadFile(file);
    }
  };

  const handleCameraCapture = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setReuploadFile(file);
    }
  };

  const handleReupload = async () => {
    if (!reuploadFile) {
      toast.error("Please select a file");
      return;
    }
    if (!reuploadAmount || parseFloat(reuploadAmount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", reuploadFile);
      formData.append("invoice_amount", reuploadAmount);
      if (reuploadNotes) formData.append("notes", reuploadNotes);

      await axios.post(
        `${API}/contractor/upload-invoice/${selectedInvoice.job_id}`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          } 
        }
      );

      toast.success("Invoice re-uploaded successfully!");
      setReuploadModal(false);
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload invoice");
    } finally {
      setUploading(false);
    }
  };

  const getFilteredInvoices = () => {
    if (activeTab === "all") return invoices;
    return invoices.filter(i => i.status === activeTab);
  };

  const filteredInvoices = getFilteredInvoices();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/contractor")}>
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-slate-800">My Invoices</h1>
              <p className="text-sm text-muted-foreground">Track your submitted invoices</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Info Banner */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Upload className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-blue-800">Upload Invoices from Jobs</h3>
                <p className="text-sm text-blue-700">
                  To upload a new invoice, go to your <Button variant="link" className="h-auto p-0 text-blue-700 underline" onClick={() => navigate("/contractor")}>Jobs dashboard</Button> and find the completed job, then click "Upload Invoice".
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">{totals.submitted_count || 0}</div>
              <div className="text-sm text-muted-foreground">Submitted</div>
              <div className="text-xs text-slate-500">£{(totals.submitted_amount || 0).toFixed(2)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-green-600">{totals.approved_count || 0}</div>
              <div className="text-sm text-muted-foreground">Approved</div>
              <div className="text-xs text-slate-500">£{(totals.approved_amount || 0).toFixed(2)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-emerald-600">{totals.paid_count || 0}</div>
              <div className="text-sm text-muted-foreground">Paid</div>
              <div className="text-xs text-slate-500">£{(totals.paid_amount || 0).toFixed(2)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-red-600">{totals.rejected_count || 0}</div>
              <div className="text-sm text-muted-foreground">Rejected</div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full">
            <TabsTrigger value="all" className="flex-1">All ({invoices.length})</TabsTrigger>
            <TabsTrigger value="submitted" className="flex-1">Submitted</TabsTrigger>
            <TabsTrigger value="approved" className="flex-1">Approved</TabsTrigger>
            <TabsTrigger value="paid" className="flex-1">Paid</TabsTrigger>
            <TabsTrigger value="rejected" className="flex-1">Rejected</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-4 space-y-3">
            {filteredInvoices.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-medium text-slate-700 mb-1">No invoices found</h3>
                  <p className="text-sm text-muted-foreground">
                    {activeTab === "all" 
                      ? "Complete jobs and upload invoices to see them here"
                      : `No ${activeTab} invoices`}
                  </p>
                </CardContent>
              </Card>
            ) : (
              filteredInvoices.map((invoice) => {
                const config = statusConfig[invoice.status] || statusConfig.submitted;
                const StatusIcon = config.icon;
                
                return (
                  <Card key={invoice.invoice_id} data-testid={`invoice-${invoice.invoice_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={config.color}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {config.label}
                            </Badge>
                            <span className="text-sm text-muted-foreground">
                              Job #{invoice.job_id?.slice(-6)}
                            </span>
                          </div>
                          
                          <div className="space-y-1">
                            <p className="font-semibold text-lg">
                              £{invoice.amount?.toFixed(2) || "0.00"}
                            </p>
                            {invoice.invoice_number && (
                              <p className="text-sm text-slate-600">
                                Invoice #: {invoice.invoice_number}
                              </p>
                            )}
                            <p className="text-sm text-muted-foreground">
                              Submitted: {new Date(invoice.submitted_at).toLocaleDateString()}
                            </p>
                            {invoice.paid_at && (
                              <p className="text-sm text-emerald-600">
                                Paid: {new Date(invoice.paid_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                          
                          {/* Rejection reason */}
                          {invoice.status === "rejected" && invoice.rejection_reason && (
                            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                              <div className="flex items-start gap-2">
                                <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5" />
                                <div>
                                  <p className="text-sm font-medium text-red-700">Rejection Reason:</p>
                                  <p className="text-sm text-red-600">{invoice.rejection_reason}</p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Re-upload button for rejected invoices */}
                        {invoice.status === "rejected" && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openReuploadModal(invoice)}
                            data-testid={`reupload-${invoice.invoice_id}`}
                          >
                            <RefreshCw className="w-4 h-4 mr-1" />
                            Re-upload
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Re-upload Modal */}
      <Dialog open={reuploadModal} onOpenChange={setReuploadModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Re-upload Invoice</DialogTitle>
            <DialogDescription>
              Upload a corrected invoice for Job #{selectedInvoice?.job_id?.slice(-6)}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* File upload buttons */}
            <div className="space-y-2">
              <Label>Invoice File (PDF, JPG, PNG)</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Choose File
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => cameraInputRef.current?.click()}
                >
                  <Camera className="w-4 h-4 mr-2" />
                  Take Photo
                </Button>
              </div>
              
              {/* Hidden file inputs */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                className="hidden"
                onChange={handleFileSelect}
              />
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handleCameraCapture}
              />
              
              {reuploadFile && (
                <div className="flex items-center gap-2 p-2 bg-slate-100 rounded">
                  <FileText className="w-4 h-4 text-blue-600" />
                  <span className="text-sm truncate">{reuploadFile.name}</span>
                </div>
              )}
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <Label>Invoice Amount (£)</Label>
              <Input
                type="number"
                step="0.01"
                value={reuploadAmount}
                onChange={(e) => setReuploadAmount(e.target.value)}
                placeholder="0.00"
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                value={reuploadNotes}
                onChange={(e) => setReuploadNotes(e.target.value)}
                placeholder="Any additional notes about this invoice..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setReuploadModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleReupload} disabled={uploading || !reuploadFile}>
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Upload Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContractorInvoicesPage;
